import React, { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { hasAccess, formatDate, ROLE_LABELS } from "@/lib/utils2";
import PageHeader from "@/components/PageHeader";
import TicketCard from "@/components/TicketCard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, Phone, CheckCircle2, XCircle } from "lucide-react";

const STATUS_BADGE = {
  open: "bg-brand-dough/25 text-brand-ink",
  in_progress: "bg-brand/15 text-brand",
  resolved: "bg-brand/15 text-brand",
  approved: "bg-brand/15 text-brand",
  denied: "bg-brand-terracotta/15 text-brand-terracotta",
};

function AdminTicket({ ticket, onUpdate }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [status, setStatus] = useState(ticket.status);
  const [busy, setBusy] = useState(false);

  const save = async (forceStatus) => {
    setBusy(true);
    try {
      await api.put(`/tickets/${ticket.id}`, { status: forceStatus || status, note: note || null });
      toast.success("Ticket updated");
      setNote(""); setOpen(false); onUpdate();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  const isCafeReg = ticket.ticket_type === "cafe_registration";
  const sensitiveRole = ["management", "operations"].includes(ticket.requested_role);

  return (
    <div className="rounded-2xl border border-brand-line bg-white p-5" data-testid={`admin-ticket-${ticket.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <p className="font-display text-xl text-brand-ink">{ticket.case_number}</p>
            <Badge className={`rounded-full border-0 ${STATUS_BADGE[ticket.status] || ""}`}>{ticket.status}</Badge>
            {isCafeReg && <Badge className="rounded-full border-0 bg-brand-dough/25 text-brand-ink">Account request</Badge>}
          </div>
          <p className="font-semibold text-brand-ink mt-2">{ticket.subject}</p>
          {ticket.message && <p className="text-brand-muted mt-1 leading-relaxed">{ticket.message}</p>}
          <p className="text-sm text-brand-muted mt-2 flex items-center gap-1"><Phone className="w-3.5 h-3.5" /> {ticket.phone} · {formatDate(ticket.created_at)}</p>
          {isCafeReg && (
            <div className="mt-3 rounded-xl bg-brand-sidebar/60 border border-brand-line p-3 text-sm">
              <p className="text-brand-ink font-medium">{ticket.first_name} {ticket.last_name} · Store {ticket.store_number}</p>
              <p className="text-brand-muted">Requested role: {ROLE_LABELS[ticket.requested_role] || ticket.requested_role}</p>
              {sensitiveRole && <p className="text-brand-terracotta mt-1 font-medium">⚠ Call the store to validate before approving.</p>}
            </div>
          )}
        </div>
      </div>
      {ticket.notes?.length > 0 && (
        <div className="mt-3 pt-3 border-t border-brand-line space-y-2">
          {ticket.notes.map((n, i) => (
            <div key={i} className="text-sm"><span className="text-brand-ink">{n.text}</span> <span className="text-xs text-brand-muted">— {n.author}</span></div>
          ))}
        </div>
      )}
      <div className="mt-4 flex flex-wrap items-center gap-2">
        {isCafeReg && ticket.status === "open" && (
          <>
            <Button size="sm" onClick={() => save("approved")} disabled={busy} className="rounded-full brand-btn" data-testid={`approve-${ticket.id}`}><CheckCircle2 className="w-4 h-4 mr-1.5" /> Approve & create account</Button>
            <Button size="sm" variant="outline" onClick={() => save("denied")} disabled={busy} className="rounded-full border-brand-terracotta text-brand-terracotta" data-testid={`deny-${ticket.id}`}><XCircle className="w-4 h-4 mr-1.5" /> Deny</Button>
          </>
        )}
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button size="sm" variant="outline" className="rounded-full border-brand-line" data-testid={`respond-${ticket.id}`}>Respond / update</Button></DialogTrigger>
          <DialogContent className="border-brand-line">
            <DialogHeader><DialogTitle className="font-display text-2xl">Update ticket {ticket.case_number}</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Status</Label>
                <Select value={status} onValueChange={setStatus}>
                  <SelectTrigger className="mt-1.5 border-brand-line" data-testid="ticket-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Open</SelectItem>
                    <SelectItem value="in_progress">In progress</SelectItem>
                    <SelectItem value="resolved">Resolved</SelectItem>
                    {isCafeReg && <SelectItem value="approved">Approved</SelectItem>}
                    {isCafeReg && <SelectItem value="denied">Denied</SelectItem>}
                  </SelectContent>
                </Select>
              </div>
              <div><Label>Note to user (optional)</Label><Textarea value={note} onChange={(e) => setNote(e.target.value)} rows={3} className="mt-1.5 border-brand-line" data-testid="ticket-note" /></div>
            </div>
            <DialogFooter><Button onClick={() => save()} disabled={busy} className="rounded-full brand-btn" data-testid="save-ticket-button">Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}

export default function HelpDesk() {
  const { user } = useAuth();
  const isAdmin = hasAccess(user, "admin");
  const [mine, setMine] = useState([]);
  const [all, setAll] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ phone: user.phone, subject: "", message: "" });
  const [busy, setBusy] = useState(false);

  const loadMine = async () => { const { data } = await api.get("/tickets/mine"); setMine(data.tickets); };
  const loadAll = async () => { if (!isAdmin) return; const { data } = await api.get("/tickets"); setAll(data.tickets); };
  useEffect(() => { loadMine(); loadAll(); /* eslint-disable-next-line */ }, []);

  const submit = async () => {
    if (!form.subject.trim()) return toast.error("Please add a subject.");
    setBusy(true);
    try {
      await api.post("/tickets", { ...form, ticket_type: "general" });
      toast.success("Ticket submitted");
      setForm({ phone: user.phone, subject: "", message: "" });
      setOpen(false); loadMine(); loadAll();
    } catch (err) { toast.error(errMsg(err)); }
    finally { setBusy(false); }
  };

  const SubmitBtn = (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button className="rounded-full brand-btn" data-testid="new-ticket-button"><Plus className="w-4 h-4 mr-2" /> New ticket</Button></DialogTrigger>
      <DialogContent className="border-brand-line">
        <DialogHeader><DialogTitle className="font-display text-2xl">Contact the Help Desk</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Phone number</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 border-brand-line" data-testid="my-ticket-phone" /></div>
          <div><Label>Subject</Label><Input value={form.subject} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-1.5 border-brand-line" data-testid="my-ticket-subject" /></div>
          <div><Label>Message</Label><Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={4} className="mt-1.5 border-brand-line" data-testid="my-ticket-message" /></div>
        </div>
        <DialogFooter><Button onClick={submit} disabled={busy} className="rounded-full brand-btn" data-testid="submit-my-ticket">Submit</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );

  const MyTickets = (
    <div className="space-y-4" data-testid="my-tickets">
      {mine.length === 0 && <p className="text-brand-muted text-sm text-center py-10">You have no tickets.</p>}
      {mine.map((t) => <TicketCard key={t.id} ticket={t} />)}
    </div>
  );

  return (
    <div className="px-4 sm:px-6 lg:px-8 py-8 max-w-4xl">
      <PageHeader title="Help Desk" subtitle="Get support and track your requests.">{SubmitBtn}</PageHeader>
      {isAdmin ? (
        <Tabs defaultValue="admin">
          <TabsList className="bg-brand-sidebar h-auto p-1 rounded-full mb-6">
            <TabsTrigger value="admin" className="rounded-full data-[state=active]:bg-white px-5 py-2" data-testid="tab-admin-tickets">All tickets</TabsTrigger>
            <TabsTrigger value="mine" className="rounded-full data-[state=active]:bg-white px-5 py-2" data-testid="tab-my-tickets">My tickets</TabsTrigger>
          </TabsList>
          <TabsContent value="admin">
            <div className="space-y-4" data-testid="admin-tickets">
              {all.length === 0 && <p className="text-brand-muted text-sm text-center py-10">No tickets yet.</p>}
              {all.map((t) => <AdminTicket key={t.id} ticket={t} onUpdate={() => { loadAll(); loadMine(); }} />)}
            </div>
          </TabsContent>
          <TabsContent value="mine">{MyTickets}</TabsContent>
        </Tabs>
      ) : MyTickets}
    </div>
  );
}
